/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useState, useContext } from "react";
import styled, { css } from "styled-components";
import T from "./Translation";
import { useRovingTabIndex } from "../../hooks/a11y/useRovingTabIndex";
import { isActivationKey } from "../../hooks/a11y/activationKeys";

const StyledTabs = styled.div`
    display: grid;
    gap: 24px;
`;

const StyledTabsList = styled.ul`
    display: flex;
`;

const StyledTabsItem = styled.li`
    flex: 1;
    padding: 10px;
    border-bottom: 2px solid rgba(${({ theme }) => theme.colors.border.rgba});
    text-align: center;
    color: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
    cursor: pointer;
    transition: 0.2s ease;

    @media (hover: hover) {
        &:hover {
            color: rgba(${({ theme }) => theme.colors.accent.rgba});
        }
    }

    ${({ $active, theme }) =>
    $active &&
    css`
                border-color: rgba(${theme.colors.accent.rgba});
                color: rgba(${theme.colors.accent.rgba});
                font-weight: 600;
            `};
`;

const TabsContext = createContext();

const Tabs = ({ children, defaultTab }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || null);

  const handleTabClick = (id) => {
    setActiveTab(id);
  };

  return (
    <TabsContext.Provider value={{ activeTab, handleTabClick }}>
      <StyledTabs>{children}</StyledTabs>
    </TabsContext.Provider>
  );
};

const TabsList = ({ children }) => {
  const { activeTab, handleTabClick } = useContext(TabsContext);
  const childArray = React.Children.toArray(children);
  const activeIndex = Math.max(0, childArray.findIndex((child) => child.props.id === activeTab));
  const { getItemProps } = useRovingTabIndex({ count: childArray.length, orientation: "horizontal", defaultIndex: activeIndex });

  return (
    <StyledTabsList role="tablist">
      {childArray.map((child, index) =>
        React.cloneElement(child, {
          isActive: child.props.id === activeTab,
          rovingProps: getItemProps(index),
          onClick: (id) => {
            handleTabClick(id);
            child.props.onClick && child.props.onClick(id);
          },
        })
      )}
    </StyledTabsList>
  );
};

const TabsItem = ({ id, title, isActive, onClick, rovingProps = {} }) => {
  const handleKeyDown = (event) => {
    rovingProps.onKeyDown?.(event);
    if (isActivationKey(event)) {
      event.preventDefault();
      onClick(id);
    }
  };

  return (
    <StyledTabsItem
      $active={isActive}
      role="tab"
      aria-selected={isActive}
      ref={rovingProps.ref}
      tabIndex={rovingProps.tabIndex}
      onKeyDown={handleKeyDown}
      onClick={() => onClick(id)}
    >
      <T>{title}</T>
    </StyledTabsItem>
  );
};

const TabContent = ({ id, children }) => {
  const { activeTab } = useContext(TabsContext);

  if (activeTab !== id) {
    return null;
  }

  return children;
};

export { Tabs, TabsList, TabsItem, TabContent };
